import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { list, updateStatus } from 'app/api/CreditRequestService';
import UserContext from 'app/context/UserContext';
import { toast } from 'react-toastify';
import {
  Table,
  Spinner,
  Alert,
  Container,
  Form,
  Row,
  Col,
  Button,
  Modal
} from 'react-bootstrap';

function CreditRequestTable() {
  const { user } = useContext(UserContext);
  const [creditRequests, setCreditRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  // Fetch credit requests
  function getCreditRequest() {
    const config = list(user.token, page, limit);

    setLoading(true);
    setError(null);

    axios(config)
      .then(response => {
        const { data, totalRecords } = response.data;
        setCreditRequests(data);
        setTotalRecords(totalRecords);
      })
      .catch(error => {
        console.error('Error fetching credit request:', error);
        setError('Failed to fetch credit requests.');
        toast.error('Failed to fetch credit requests.');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    getCreditRequest();
  }, [page, limit]);

  const handleStatusChange = (id, newStatus) => {
    if (newStatus === 'Reject') {
      setSelectedRequestId(id);
      setShowModal(true);
      return;
    }

    sendStatusUpdate(id, newStatus);
  };

  const confirmRejection = () => {
    if (selectedRequestId && rejectionMessage.trim()) {
      sendStatusUpdate(selectedRequestId, 'Reject', rejectionMessage);
      setShowModal(false);
      setRejectionMessage('');
      setSelectedRequestId(null);
    }
  };

  const sendStatusUpdate = (id, status, rejectionMessage = '') => {
    // Optimistic UI update
    const updatedRequests = creditRequests.map(item =>
      item.id === id ? { ...item, status } : item
    );
    setCreditRequests(updatedRequests);

    const payload = {
      id,
      status,
      ...(status === 'Reject' && { rejectionMessage }),
    };

    const config = updateStatus(user.token, payload);

    axios(config)
      .then(() => {
        toast.success(`Status updated to ${status}`);
      })
      .catch(err => {
        console.error('Error updating status:', err);
        toast.error('Failed to update status');
      });
  };

  // Pagination Logic
  const startRecord = (page - 1) * limit + 1;
  const endRecord = Math.min(page * limit, totalRecords);
  const totalPages = Math.ceil(totalRecords / limit);

  const handleLimitChange = e => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handlePrev = () => {
    if (page > 1) setPage(prev => prev - 1);
  };

  const handleNext = () => {
    if (page < totalPages) setPage(prev => prev + 1);
  };

  return (
    <Container className="mt-4">
      {loading && (
        <div className="text-center">
          <Spinner animation="border" role="status" />
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && (
        <>
          <Table striped bordered hover responsive>
            <thead className="bg-200 text-900">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Credit Amount</th>
                <th>Purchase Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {creditRequests.length > 0 ? (
                creditRequests.map(item => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.email}</td>
                    <td>{item.creditAmount}</td>
                    <td>{new Date(item.purchaseDate).toLocaleDateString()}</td>
                    <td>
                      {item.status === 'Pending' ? (
                        <Form.Select
                          value={item.status}
                          onChange={e =>
                            handleStatusChange(item.id, e.target.value)
                          }
                          size="sm"
                        >
                          <option value="Pending">Pending</option>
                          <option value="Accept">Accept</option>
                          <option value="Reject">Reject</option>
                        </Form.Select>
                      ) : (
                        item.status
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No credit requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Row className="align-items-center justify-content-between mt-3">
            <Col xs="auto">
              <span className="me-2">
                {startRecord}–{endRecord} of {totalRecords}
              </span>
              <Form.Select
                value={limit}
                onChange={handleLimitChange}
                size="sm"
                style={{ display: 'inline-block', width: 'auto' }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
              </Form.Select>
              <span className="ms-2">Rows per page</span>
            </Col>

            <Col xs="auto">
              <Button
                variant="primary"
                size="sm"
                className="me-2"
                onClick={handlePrev}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleNext}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </Col>
          </Row>
        </>
      )}

      {/* Rejection Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Rejection Reason</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Please enter a reason for rejection:</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectionMessage}
              onChange={(e) => setRejectionMessage(e.target.value)}
              placeholder="Enter reason here..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmRejection}
            disabled={!rejectionMessage.trim()}
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CreditRequestTable;
